import { ClientAddress, ClientProfile } from '../services/trustline';

function filled(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/** Core address fields that must be present for a complete client profile. */
export function isClientAddressComplete(address?: ClientAddress | null): boolean {
  if (!address || typeof address !== 'object') return false;
  return (
    filled(address.line1) &&
    filled(address.city) &&
    filled(address.postalCode) &&
    filled(address.country)
  );
}

export function getMissingClientProfileFields(
  profile: ClientProfile | null | undefined
): string[] {
  const missing: string[] = [];
  if (!profile || typeof profile !== 'object') {
    return ['type', 'firstName', 'lastName', 'address'];
  }
  if (!filled(profile.type)) missing.push('type');
  if (!filled(profile.firstName)) missing.push('firstName');
  if (!filled(profile.lastName)) missing.push('lastName');
  if (!isClientAddressComplete(profile.address)) missing.push('address');
  return missing;
}

/**
 * Profile is complete when type, first/last name, and core address fields are set.
 * Missing / empty profile → incomplete.
 */
export function isClientProfileComplete(profile: ClientProfile | null | undefined): boolean {
  return getMissingClientProfileFields(profile).length === 0;
}

/** Normalize getClientProfile RPC payloads (nested `profile` or flat document). */
export function extractClientProfile(data: unknown): ClientProfile | null {
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;

  if (record.profile && typeof record.profile === 'object') {
    return record.profile as ClientProfile;
  }

  // Flat document: must look like a client profile, not just `{ success: true }`.
  if (typeof record.clientId === 'string') {
    return {
      clientId: record.clientId,
      email: typeof record.email === 'string' ? record.email : undefined,
      createdAt: typeof record.createdAt === 'number' ? record.createdAt : undefined,
      type: typeof record.type === 'string' ? record.type : undefined,
      firstName: typeof record.firstName === 'string' ? record.firstName : undefined,
      lastName: typeof record.lastName === 'string' ? record.lastName : undefined,
      entityName: typeof record.entityName === 'string' ? record.entityName : undefined,
      title: typeof record.title === 'string' ? record.title : undefined,
      address:
        record.address && typeof record.address === 'object'
          ? (record.address as ClientAddress)
          : undefined,
    };
  }

  return null;
}
